import type { FC, ReactNode } from 'react'
import type { TelegramContact } from '@/lib/marketplace/telegram-contact'

import { useState } from 'react'
import { Check, Copy, ExternalLink, Link2, MessageCircleOff } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { trackEvent } from '@/lib/analytics/client'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'
import { Button, buttonVariants } from '@/components/ui/button'

interface ContactButtonProps {
    listingId: string
    sellerId: string
    /**
     * The RESOLVED contact, decided server-side by `resolveTelegramContact`.
     * Never a raw column: this component chooses its wording from `kind`, and a
     * caller that resolved for itself is a caller that can disagree with the
     * console.
     */
    contact: TelegramContact | null
    disabled?: boolean
    disabledReason?: string
    className?: string
    size?: 'default' | 'large'
    /**
     * Optional ready-to-send enquiry. Telegram cannot prefill a message when
     * you deep link to a person (only to a bot), so when this is provided the
     * text is copied to the clipboard as the link opens and the buyer pastes
     * it. That is what makes the quantity selector do real work.
     */
    enquiry?: string
}

/**
 * The single most important action on the site. Records the click before
 * handing off to Telegram, so click-to-contact rate is measurable even
 * though the conversation itself happens off platform.
 */
export const ContactButton: FC<ContactButtonProps> = ({
    listingId,
    sellerId,
    contact,
    disabled,
    disabledReason,
    className,
    size = 'default',
    enquiry
}): ReactNode => {
    const height = size === 'large' ? 'h-14 text-base' : 'h-12 text-sm'

    const handleClick = (): void => {
        trackEvent({ type: 'telegram_contact_click', listingId, sellerId })

        // A permalink lands the buyer on the post, not in a chat, so there is
        // nothing to paste and copying would only confuse them.
        if (!enquiry || contact?.kind !== 'handle') return

        // Never block the hand-off to Telegram on the clipboard succeeding.
        void navigator.clipboard?.writeText(enquiry)
            .then(() => toast.success('Enquiry copied', { description: 'Paste it into the chat that just opened.' }))
            .catch(() => undefined)
    }

    if (disabled) {
        return (
            <div className={cn('flex w-full flex-col gap-1.5', className)}>
                <Button disabled className={cn(height, 'w-full font-semibold')}>
                    <MessageCircleOff className='size-4' aria-hidden='true' />
                    Not available
                </Button>

                {disabledReason && (
                    <p className='text-center text-xs text-muted-foreground'>
                        {disabledReason}
                    </p>
                )}
            </div>
        )
    }

    /*
     * Nothing resolved at all. A listing in this state should not normally be
     * public — ingestion holds it for review instead — so this is the rare
     * legacy row, and it says so quietly in one line rather than shouting it in
     * a disabled box the size of the primary action.
     *
     * `className` is deliberately NOT applied: every call site passes
     * button-shaped sizing (a fixed h-13 and an 11px type scale), which is what
     * squashed the old block until its two paragraphs overlapped.
     */
    if (!contact) {
        return (
            <p className='flex min-h-11 items-center justify-center gap-2 text-center text-xs text-text-muted'>
                <MessageCircleOff className='size-4 shrink-0' aria-hidden='true' />
                Telegram contact unavailable
            </p>
        )
    }

    /*
     * A permalink reaches the POST, not the person, so it must never be worded
     * as though it opens a conversation. Same shape, honest label.
     */
    const isPermalink = contact.kind === 'permalink'

    return (
        /*
         * A real anchor, styled with `buttonVariants`. This NAVIGATES — it hands
         * the buyer to Telegram — so it must keep link semantics: announced as a
         * link, opened with ctrl/middle click, and offered "open in new tab" by
         * the context menu. Wrapping it in Base UI's `Button` applied native
         * button semantics on top of an anchor and took all of that away.
         *
         * `handleClick` is a side effect only (analytics, then a best-effort
         * clipboard write). It never calls `preventDefault`, so the navigation
         * is the browser's and is unaffected.
         */
        <a
            href={contact.url}
            target='_blank'
            rel='noopener noreferrer'
            onClick={handleClick}
            className={cn(
                buttonVariants({ variant: isPermalink ? 'outline' : 'default' }),
                height,
                'w-full font-semibold',
                className
            )}
        >
            {isPermalink
                ? <ExternalLink className={size === 'large' ? 'size-5' : 'size-4'} aria-hidden='true' />
                : <TelegramGlyph className={size === 'large' ? 'size-5' : 'size-4'} />}
            {isPermalink ? 'Open original Telegram post' : 'Message on Telegram'}
        </a>
    )
}

interface ShareButtonProps {
    title: string
    listingId: string
    className?: string
}

export const ShareButton: FC<ShareButtonProps> = ({ title, listingId, className }): ReactNode => {
    const [copied, setCopied] = useState(false)

    const share = async (): Promise<void> => {
        const url = window.location.href

        trackEvent({ type: 'share_click', listingId })

        if (typeof navigator.share === 'function') {
            try {
                await navigator.share({ title, url })

                return
            } catch {
                // The visitor dismissed the share sheet. Fall through to copying.
            }
        }

        try {
            await navigator.clipboard.writeText(url)

            setCopied(true)
            toast.success('Link copied')

            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            toast.error('Could not copy the link', { description: 'Copy it from the address bar instead.' })
        }
    }

    return (
        <Button
            variant='outline'
            onClick={share}
            className={cn('h-10 px-3 text-sm', className)}
        >
            {copied
                ? <Check className='size-4' aria-hidden='true' />
                : <Link2 className='size-4' aria-hidden='true' />}
            {copied ? 'Copied' : 'Share'}
        </Button>
    )
}

export const CopyButton: FC<{ value: string, label?: string, className?: string }> = ({ value, label = 'Copy', className }): ReactNode => {
    const [copied, setCopied] = useState(false)

    const copy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(value)

            setCopied(true)
            window.setTimeout(() => setCopied(false), 1600)
        } catch {
            toast.error('Could not copy to the clipboard')
        }
    }

    return (
        <Button variant='outline' onClick={copy} className={cn('h-9 px-3 text-xs', className)}>
            {copied
                ? <Check className='size-3.5' aria-hidden='true' />
                : <Copy className='size-3.5' aria-hidden='true' />}
            {copied ? 'Copied' : label}
        </Button>
    )
}
